import { tz } from '@date-fns/tz';
import { format, parseISO } from 'date-fns';

import { getDateFnsLocale } from './dateFnsLocale';

/**
 * Calendar-date style options. Extend this map — not the caller code — when
 * a new visual style is needed. Keeping styles as data (not per-locale
 * `switch`es inside the formatter) is the whole point of the shape here.
 */
const STYLE_PATTERNS = {
	slashed: { enUS: 'MM/dd/yyyy', other: 'dd/MM/yyyy' },
	dotted: { enUS: 'MM.dd.yyyy', other: 'dd.MM.yyyy' },
};

const DEFAULT_STYLE = 'slashed';
const US_LOCALE_CODE = 'en-US';

/**
 * Calendar date in the caller's locale style.
 *
 * - `en-US` → month-first (`"01/22/2026"` slashed, `"01.22.2026"` dotted).
 * - Every other locale → day-first (`"22/01/2026"` slashed, `"22.01.2026"`
 *   dotted).
 *
 * No time part — pair with `formatTime` when the caller needs both.
 * The `style` option lets callers pick the separator without duplicating
 * locale-branching logic in their own file.
 *
 * @param {string | Date | null | undefined} datetime UTC ISO or `Date`.
 * @param {{ timezone?: string, locale?: string, style?: 'slashed' | 'dotted' }} [options]
 *   - `timezone` — IANA zone (e.g. `"Europe/Berlin"`). Defaults to the
 *     browser's zone when omitted.
 *   - `locale` — BCP-47 tag. Defaults to `navigator.language`.
 *   - `style` — `"slashed"` (default) or `"dotted"`. Unknown values fall back
 *     to `"slashed"` so a typo doesn't crash the render.
 * @returns {string} rendered date, or `""` for a bad input.
 */
export const formatDateDisplay = (datetime, { timezone, locale, style = DEFAULT_STYLE } = {}) => {
	if (!datetime) return '';
	const parsed = typeof datetime === 'string' ? parseISO(datetime) : datetime;
	if (Number.isNaN(parsed?.getTime?.())) return '';

	const dateFnsLocale = getDateFnsLocale(locale);
	const pattern = (STYLE_PATTERNS[style] ?? STYLE_PATTERNS[DEFAULT_STYLE])[
		dateFnsLocale.code === US_LOCALE_CODE ? 'enUS' : 'other'
	];

	const options = { locale: dateFnsLocale };
	if (timezone) options.in = tz(timezone);

	return format(parsed, pattern, options);
};
